import type { ListRuleVersionsResponse, RuleListItem } from "@wallyo/contracts/console/configuration";
import type { DefinedRuleId } from "@wallyo/contracts/rules";
import { useEffect, useState } from "react";
import { listRuleVersions, listRules } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { selectedId } from "../config-route";
import { toLocalInput } from "../local-datetime";
import { useIntentWrite } from "../team/TeamDialogs";
import {
  emptyVersionFields,
  ruleLock,
  ruleVersionProblem,
  sendRuleVersion,
  versionRequest,
  type RuleVersionFields,
} from "./rules-model";
import { RuleDetail, RuleList, RuleVersionFormView } from "./RulesView";

/** The screen's own words for each refusal the server can give on a rule. */
export const RULE_REFUSALS = {
  critical: "rules.refused.critical",
  effective_date_in_past: "rules.refused.effectivePast",
  legal_basis_required: "rules.refused.legalBasis",
  invalid_value: "rules.refused.invalidValue",
  rule_retired: "rules.refused.retired",
} as const;

export const describeRuleFailure = (error: unknown) => describeConfigFailure(error, RULE_REFUSALS, "rules.stale");

/**
 * Règles (EDIT_RULES): `#rules` lists the company rules, `#rules/<ruleId>`
 * shows one and its versions. A new version asks for a reason (and a legal
 * basis where the rule needs one), carries one Idempotency-Key per intent and
 * the rule's version, and shows only once the server has answered.
 */
export function RulesScreen() {
  const { client } = useApi();
  const { me } = useStaff();
  const hash = useHash();
  const ruleId = selectedId(hash, "rules");
  const list = useLoad(() => listRules(client), "list");
  const versions = useLoad<ListRuleVersionsResponse>(
    ruleId ? () => listRuleVersions(client, ruleId as DefinedRuleId) : null,
    ruleId,
  );
  const [editing, setEditing] = useState(false);
  const alerts = useAlerts();
  const roles = me.staffMember.roles.map((role) => role.name);

  useEffect(() => {
    setEditing(false);
  }, [ruleId]);

  const reloadAll = () => {
    list.reload();
    versions.reload();
  };

  const rules = list.state.data?.rules ?? [];
  const detail = versions.state.data && versions.state.data.rule.ruleId === ruleId ? versions.state.data : null;
  const known = rules.some((rule) => rule.ruleId === ruleId);
  const now = new Date();

  return (
    <>
      <PageTop title={t("screen.rules")} sub={t("sub.rules")} />
      {list.state.status === "failed" ? (
        <LoadFailed
          message={t("rules.loadFailed")}
          retry={t("rules.retry")}
          error={list.state.error}
          onRetry={list.reload}
        />
      ) : null}
      {!list.state.data && list.state.status === "loading" ? <Loading text={t("rules.loading")} /> : null}
      {list.state.data ? (
        <div className="split list">
          <RuleList rules={rules} selectedId={ruleId} />
          {!ruleId ? (
            <div className="card">
              <div className="empty">{t("rules.pick")}</div>
            </div>
          ) : versions.state.status === "failed" && !detail ? (
            <div className="card">
              {known ? (
                <LoadFailed
                  message={t("rules.loadFailed")}
                  retry={t("rules.retry")}
                  error={versions.state.error}
                  onRetry={versions.reload}
                />
              ) : (
                <div className="empty">{t("rules.notFound")}</div>
              )}
            </div>
          ) : !detail ? (
            <div className="card">
              <Loading text={t("rules.loading")} />
            </div>
          ) : (
            <RuleDetail
              rule={detail.rule}
              versions={detail.versions}
              lock={ruleLock(detail.rule, me.features)}
              roles={roles}
              now={now}
              onNew={() => setEditing(true)}
              editor={
                editing ? (
                  <RuleVersionEditor
                    rule={detail.rule}
                    onCancel={() => setEditing(false)}
                    onReload={() => {
                      setEditing(false);
                      reloadAll();
                    }}
                    onDone={(message) => {
                      alerts.ok(message);
                      setEditing(false);
                      reloadAll();
                    }}
                  />
                ) : undefined
              }
            />
          )}
        </div>
      ) : null}
    </>
  );
}

function RuleVersionEditor(props: {
  rule: RuleListItem;
  onCancel: () => void;
  onReload: () => void;
  onDone: (message: string) => void;
}) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [fields, setFields] = useState<RuleVersionFields>(() => emptyVersionFields(props.rule));
  const change = (next: Partial<RuleVersionFields>) => {
    write.edited();
    setFields((current) => ({ ...current, ...next }));
  };
  const submit = () => {
    const problem = ruleVersionProblem(props.rule, fields, new Date());
    void write.run(
      problem,
      (key) => sendRuleVersion(client, props.rule, versionRequest(props.rule, fields), key),
      ({ ruleVersion }) =>
        props.onDone(
          new Date(ruleVersion.effectiveFrom).getTime() > Date.now()
            ? t("rules.done.scheduled", {
                version: ruleVersion.version,
                date: formatDateTime(ruleVersion.effectiveFrom),
              })
            : t("rules.done.now", { version: ruleVersion.version }),
        ),
    );
  };
  return (
    <RuleVersionFormView
      rule={props.rule}
      fields={fields}
      onChange={change}
      minEffective={toLocalInput(new Date())}
      pending={write.pending}
      problem={write.problem}
      failure={write.error ? describeRuleFailure(write.error) : null}
      onSubmit={submit}
      onCancel={props.onCancel}
      onReload={props.onReload}
    />
  );
}
